import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopoverPlacement } from './usePopoverPlacement'
import { createSearcher, normalizeForSearch } from '../../lib/fuzzySearch'

export interface ComboboxOption {
  value: string
  label: string
}

interface ComboboxProps {
  label?: string
  labelClassName?: string
  error?: string
  options: ComboboxOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  id?: string
  ariaLabel?: string
  disabled?: boolean
  className?: string
  /** Larghezza massima del menu, in pixel. */
  maxWidth?: number
  /** Mostra la barra di ricerca fuzzy. Default: solo se ci sono più di 6 opzioni - per liste corte
   * (es. Tutti/Privato/Azienda) la ricerca è superflua, ma il pannello resta comunque custom, non nativo. */
  searchable?: boolean
  /** Modalità async (ricerca lato server, es. autocomplete clienti): quando fornito, il filtro
   * fuzzy locale è disattivato, le opzioni sono usate così come arrivano e ogni digitazione
   * viene inoltrata a onSearchTextChange. Implica la barra di ricerca. */
  searchText?: string
  onSearchTextChange?: (query: string) => void
  /** Nota in fondo al pannello (es. "Mostrati 20 di 45 - affina la ricerca"): serve nei
   * dropdown async dove il server pagina e non tutti i match compaiono. */
  footerNote?: string
}

/** Chiavi Fuse stabili a module scope (vedi lib/fuzzySearch: l'indice si memoizza). */
const OPTION_KEYS = ['label'] as const

export function Combobox({ label, labelClassName, error, options, value, onChange, placeholder, id, ariaLabel, disabled, className = '', maxWidth, searchable, searchText, onSearchTextChange, footerNote }: ComboboxProps) {
  const generatedId = useId()
  const controlId = id ?? generatedId
  const isAsync = onSearchTextChange != null
  const showSearch = searchable ?? (isAsync || options.length > 6)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)
  const { open, setOpen, anchorRef: containerRef, placementRef, popRef, panelStyle, portalTarget } = usePopoverPlacement({
    matchAnchorWidth: true,
    maxWidth,
    onDismiss: () => setQuery(''),
  })

  const selected = options.find((o) => o.value === value)

  // Indice Fuse memoizzato sulle opzioni: tollera refusi ("scarlati" -> "Scarlatti")
  // e piega gli accenti, come lo shared/search di gustovo-platform.
  const searcher = useMemo(() => createSearcher(options, [...OPTION_KEYS]), [options])

  const filtered = useMemo(() => {
    // In modalità async il filtro è compito del chiamante (query server): nessuna scrematura locale,
    // altrimenti digitando si nasconderebbero i risultati appena arrivati.
    if (isAsync || !normalizeForSearch(query)) return options
    return searcher.search(normalizeForSearch(query)).map((r) => r.item)
  }, [options, searcher, query, isAsync])

  useEffect(() => {
    if (open) {
      setHighlighted(0)
      if (showSearch) requestAnimationFrame(() => searchRef.current?.focus())
    }
  }, [open, showSearch])

  function selectOption(option: ComboboxOption) {
    onChange(option.value)
    setOpen(false)
    setQuery('')
    // In modalità async il testo di ricerca è di proprietà del chiamante e resta
    // invariato: alla riapertura si ritrova il contesto che ha prodotto la scelta.
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false)
      setQuery('')
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const option = filtered[highlighted]
      if (option) selectOption(option)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label htmlFor={controlId} className={`mb-1 block text-sm font-medium text-body ${labelClassName ?? ''}`}>
          {label}
        </label>
      )}
      <button
        ref={(element) => { placementRef.current = element }}
        id={controlId}
        aria-label={ariaLabel}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={!showSearch ? onKeyDown : undefined}
        className={`flex w-full cursor-pointer items-center justify-between rounded-md border bg-canvas px-3 py-2 font-sans text-left text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:cursor-not-allowed disabled:bg-surface disabled:text-faint ${error ? 'border-danger-500' : 'border-border-strong'} ${className}`}
      >
        <span className={`min-w-0 truncate ${selected ? '' : 'text-muted'}`}>{selected?.label ?? placeholder ?? 'Seleziona...'}</span>
        <svg className="h-4 w-4 shrink-0 text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {error && <p className="mt-1 text-xs text-negative">{error}</p>}

      {open && portalTarget && createPortal(
        <div ref={popRef} style={panelStyle} className="pointer-events-auto fixed z-[var(--z-index-tooltip)] overflow-hidden rounded-md border border-border bg-canvas shadow-lg">
          {showSearch && (
            <div className="border-b border-border-subtle p-2">
              <input
                ref={searchRef}
                type="text"
                value={isAsync ? (searchText ?? '') : query}
                onChange={(e) => {
                  if (isAsync) onSearchTextChange?.(e.target.value)
                  else setQuery(e.target.value)
                }}
                onKeyDown={onKeyDown}
                placeholder="Cerca..."
                className="w-full rounded-md border border-border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              />
            </div>
          )}
          <ul className="max-h-60 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 && <li className="px-3 py-2 text-sm text-muted">Nessun risultato</li>}
            {filtered.map((option, i) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  onMouseEnter={() => setHighlighted(i)}
                  onClick={() => selectOption(option)}
                  className={`block w-full cursor-pointer px-3 py-1.5 text-left text-sm ${
                    i === highlighted ? 'bg-primary-50 text-link' : 'text-body'
                  } ${option.value === value ? 'font-medium' : ''}`}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
          {footerNote && (
            <div className="border-t border-border-subtle bg-surface px-3 py-1.5 text-xs text-muted">
              {footerNote}
            </div>
          )}
        </div>, portalTarget,
      )}
    </div>
  )
}
